import { useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, CardActionArea,
  Chip, Grid, Alert, Skeleton, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
import UploadIcon from '@mui/icons-material/Upload'
import RefreshIcon from '@mui/icons-material/Refresh'
import AdminPageWrapper from '../../components/AdminPageWrapper'
import { useAgentList } from './hooks/useAgentPrompts'
import AddAgentDialog from './components/AddAgentDialog'
import ReseedDialog from './components/ReseedDialog'
import { adminAgentsAPI } from '../../services/api'

function AgentCard({ agent, navigate, isSubAgent, onDelete }) {
  const [hovered, setHovered] = useState(false)
  return (
    <Card
      variant="outlined"
      sx={isSubAgent ? { borderStyle: 'dashed', ml: 2, position: 'relative' } : { position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CardActionArea onClick={() => navigate(`/admin/agent-builder/${encodeURIComponent(agent.name)}`)}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isSubAgent
            ? <AccountTreeIcon color="action" fontSize="small" />
            : <SmartToyIcon color="primary" />}
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {agent.name.replace(/_/g, ' ')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={agent.name} size="small" variant="outlined" />
              {isSubAgent && (
                <Chip label="sub-agent" size="small" color="secondary" variant="outlined" />
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
      {hovered && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(agent) }}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'error.main', opacity: 0.7 }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Card>
  )
}

export default function AgentBuilderLandingPage() {
  const navigate = useNavigate()
  const { agents, loading, error } = useAgentList()
  const [addOpen, setAddOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState(null)
  const importInputRef = useRef(null)
  const [deleteCandidate, setDeleteCandidate] = useState(null) // { name, parentAgent, subAgentCount }
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [reseedDialogOpen, setReseedDialogOpen] = useState(false)
  const [reseedResult, setReseedResult] = useState(null) // { count } or { error }
  const [deletingOrphans, setDeletingOrphans] = useState(false)

  // Group: orchestrators first, then sub-agents nested under their parent.
  // A sub-agent is "orphaned" if its parent_agent is missing OR points to a
  // name that no longer exists as an orchestrator (e.g., parent was deleted
  // but the sub-agent registration wasn't cleaned up).
  const { orchestrators, subAgentsByParent, orphaned } = useMemo(() => {
    const orchestrators = agents.filter(a => !a.is_sub_agent)
    const orchestratorNames = new Set(orchestrators.map(a => a.name))
    const subAgentsByParent = {}
    const orphaned = []
    agents.filter(a => a.is_sub_agent).forEach(a => {
      if (a.parent_agent && orchestratorNames.has(a.parent_agent)) {
        if (!subAgentsByParent[a.parent_agent]) subAgentsByParent[a.parent_agent] = []
        subAgentsByParent[a.parent_agent].push(a)
      } else {
        orphaned.push(a)
      }
    })
    return { orchestrators, subAgentsByParent, orphaned }
  }, [agents])

  const handleImport = async (e) => {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    setImporting(true)
    setImportError(null)
    try {
      const text = await file.text()
      const bundle = JSON.parse(text)
      const result = await adminAgentsAPI.importAgentBundle(bundle)
      navigate(`/admin/agent-builder/${encodeURIComponent(result.agent)}`)
    } catch {
      setImportError('Failed to import agent. Make sure the file is a valid agent export.')
    } finally {
      setImporting(false)
    }
  }

  const handleReseedComplete = (result) => {
    setReseedDialogOpen(false)
    setReseedResult({ count: result?.seeded ?? 0 })
    if ((result?.seeded ?? 0) > 0) navigate(0)
  }

  const handleDownloadSchema = async () => {
    try {
      const content = await adminAgentsAPI.downloadSchema()
      const blob = new Blob([content], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'agent-builder-schema.md'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setImportError('Failed to download schema. Please try again.')
    }
  }

  const handleDeleteAllOrphans = async () => {
    setDeletingOrphans(true)
    try {
      await Promise.all(orphaned.map(a => adminAgentsAPI.deleteAgent(a.name)))
      navigate(0)
    } catch {
      setDeletingOrphans(false)
    }
  }

  const handleDeleteConfirm = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await adminAgentsAPI.deleteAgent(deleteCandidate.name)
      setDeleteCandidate(null)
      navigate(0) // reload to refresh list
    } catch {
      setDeleteError('Failed to delete agent')
      setDeleting(false)
    }
  }

  const totalAgents = agents.length

  return (
    <AdminPageWrapper
      title="Agent Builder"
      subtitle="Select an agent to open its prompt canvas"
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {importError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>{importError}</Alert>}
      {reseedResult && !reseedResult.error && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReseedResult(null)}>
          {reseedResult.count > 0
            ? `Seeded ${reseedResult.count} agent(s) from JSON.`
            : 'No agents needed seeding — all already have a canvas.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => setReseedDialogOpen(true)}
          title="Re-seed agents from JSON — choose idempotent or forced mode"
        >
          Re-seed from JSON
        </Button>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={handleDownloadSchema}
          title="Download the agent builder schema as a reference for AI-assisted editing"
        >
          AI Schema
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => importInputRef.current.click()}
          disabled={importing}
        >
          {importing ? 'Importing…' : 'Import'}
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          New Agent
        </Button>
      </Box>
      <input
        ref={importInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleImport}
      />

      <AddAgentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(agent) => {
          navigate(`/admin/agent-builder/${encodeURIComponent(agent.name)}`)
        }}
      />

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ minWidth: 100 }}>
          <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h5" color="primary.main" fontWeight={700}>
              {loading ? <Skeleton width={30} sx={{ mx: 'auto' }} /> : totalAgents}
            </Typography>
            <Typography variant="caption" color="text.secondary">Agents</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Agent grid */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {/* Orchestrator agents with their sub-agents below */}
          {orchestrators.map(agent => (
            <Grid item xs={12} key={agent.name}>
              <AgentCard
                agent={agent}
                navigate={navigate}
                isSubAgent={false}
                onDelete={(a) => setDeleteCandidate({ name: a.name, parentAgent: null, subAgentCount: subAgentsByParent[a.name]?.length || 0 })}
              />
              {subAgentsByParent[agent.name]?.length > 0 && (
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  {subAgentsByParent[agent.name].map(sub => (
                    <Grid item xs={12} sm={6} md={4} key={sub.name}>
                      <AgentCard
                        agent={sub}
                        navigate={navigate}
                        isSubAgent
                        onDelete={(a) => setDeleteCandidate({ name: a.name, parentAgent: a.parent_agent || null, subAgentCount: 0 })}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          ))}

          {/* Orphaned sub-agents — either no parent_agent or parent doesn't
              exist in the agents container. Surfaced so they can be deleted
              if registered in error. */}
          {orphaned.length > 0 && (
            <Grid item xs={12}>
              <Alert
                severity="warning"
                sx={{ mt: 2, mb: 1 }}
                action={
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteAllOrphans}
                    disabled={deletingOrphans}
                  >
                    {deletingOrphans ? 'Deleting…' : 'Delete all'}
                  </Button>
                }
              >
                <strong>{orphaned.length} orphaned sub-agent{orphaned.length > 1 ? 's' : ''}</strong> — registered as a sub-agent but the parent is missing or unknown. Delete any that exist in error.
              </Alert>
              <Grid container spacing={1}>
                {orphaned.map(agent => (
                  <Grid item xs={12} sm={6} md={4} key={agent.name}>
                    <AgentCard
                      agent={agent}
                      navigate={navigate}
                      isSubAgent
                      onDelete={(a) => setDeleteCandidate({ name: a.name, parentAgent: a.parent_agent || null, subAgentCount: 0 })}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {!loading && agents.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No agents found. Click New Agent to get started.
        </Typography>
      )}

      <Dialog open={!!deleteCandidate} onClose={() => !deleting && setDeleteCandidate(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 14 }}>Delete {deleteCandidate?.name}?</DialogTitle>
        <DialogContent>
          {deleteCandidate?.parentAgent && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 11 }}>
              This is a sub-agent of <strong>{deleteCandidate.parentAgent}</strong>. Any <code>@{deleteCandidate.name}</code> references in that agent's prompts will be highlighted as errors after deletion.
            </Alert>
          )}
          {deleteCandidate?.subAgentCount > 0 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 11 }}>
              This agent has <strong>{deleteCandidate.subAgentCount} sub-agent{deleteCandidate.subAgentCount > 1 ? 's' : ''}</strong> which will also be permanently deleted.
            </Alert>
          )}
          <DialogContentText sx={{ fontSize: 12 }}>This action cannot be undone.</DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 1, fontSize: 11 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDeleteCandidate(null)} disabled={deleting}>Cancel</Button>
          <Button size="small" color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <ReseedDialog
        open={reseedDialogOpen}
        onClose={() => setReseedDialogOpen(false)}
        onComplete={handleReseedComplete}
      />
    </AdminPageWrapper>
  )
}
